import { useState } from "react";
import Head from "next/head";
import { createClient } from "../../prismicio";
import { Icon } from "@/components";
import { ICONS } from "@/components/Icon/icons.config.mjs";
import Styles from "./icons.module.css";

/**
 * Icon library — every public icon name authors can pick in Prismic.
 *
 * Pulls the catalog straight from components/Icon/icons.config.mjs (no
 * parsing, no duplication), grouped by category. Clicking a tile copies the
 * name for pasting into a Slice Machine Select or an `icon` prop. Committed
 * on purpose (same pattern as PTG's /component-library/icons) so editors can
 * always see what's available; noindexed.
 */
const CATEGORIES = [...new Set(Object.values(ICONS).map((e) => e.category))].sort();

const byCategory = CATEGORIES.map((category) => ({
	category,
	names: Object.keys(ICONS)
		.filter((name) => ICONS[name].category === category)
		.sort(),
}));

export default function IconLibrary() {
	const [copied, setCopied] = useState(null);

	const handleCopy = async (name) => {
		try {
			await navigator.clipboard.writeText(name);
			setCopied(name);
			setTimeout(() => setCopied((prev) => (prev === name ? null : prev)), 1500);
		} catch {
			/* clipboard unavailable (http, permissions) — the tile still shows the name */
		}
	};

	return (
		<>
			<Head>
				<title>Icon Library — Tech Safety Lines</title>
				<meta name="robots" content="noindex, nofollow" />
			</Head>

			<main className={Styles.wrapper}>
				<div className={Styles.container} data-contain="true">
					<p className={Styles.eyebrow}>Component Library</p>
					<h1 className={Styles.heading}>Icons</h1>
					<p className={Styles.intro}>
						{Object.keys(ICONS).length} icons available in Prismic icon fields
						and the <code>Icon</code> component. Click any tile to copy its
						name. To add one: import it in{" "}
						<code>components/Icon/icons.config.mjs</code>, add a registry
						entry, then run <code>npm run sync-icons</code>.
					</p>

					{byCategory.map(({ category, names }) => (
						<section key={category} className={Styles.section}>
							<h2 className={Styles.sectionHeading}>
								{category}
								<span className={Styles.count}>{names.length}</span>
							</h2>
							<ul className={Styles.grid}>
								{names.map((name) => (
									<li key={name}>
										<button
											type="button"
											className={Styles.tile}
											onClick={() => handleCopy(name)}
											title={`Copy "${name}"`}
										>
											<span className={Styles.glyph}>
												<Icon icon={name} size={24} />
											</span>
											<span className={Styles.name}>
												{copied === name ? "Copied!" : name}
											</span>
										</button>
									</li>
								))}
							</ul>
						</section>
					))}
				</div>
			</main>
		</>
	);
}

export async function getStaticProps({ previewData }) {
	// Stale preview tokens 500 every fetch — fall back to the master ref.
	try {
		const client = createClient({ previewData });
		const nav = await client.getSingle("nav");
		const footer = await client.getSingle("footer");
		return { props: { nav, footer } };
	} catch (error) {
		const client = createClient();
		const nav = await client.getSingle("nav");
		const footer = await client.getSingle("footer");
		return { props: { nav, footer } };
	}
}
